import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Heart, MessageCircle, Trophy, Send, Loader2, Trash2, User as UserIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function relativeTime(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function AuthorHeader({ author, createdAt }) {
  if (!author) return null;
  const canLink = author.is_public && author.slug;
  const content = (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="w-9 h-9 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden flex-shrink-0">
        {author.profile_photo ? (
          <img src={resolveImageUrl(author.profile_photo)} alt="" className="w-full h-full object-cover" />
        ) : (
          <UserIcon className="w-4 h-4 text-zinc-500" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm text-zinc-100 font-medium truncate">{author.name}</p>
        <p className="text-[10px] text-zinc-500 font-mono">{relativeTime(createdAt)}</p>
      </div>
    </div>
  );
  return canLink ? (
    <Link to={`/collector/${author.slug}`} className="group hover:opacity-90 transition-opacity" data-testid={`haul-author-${author.user_id}`}>
      {content}
    </Link>
  ) : content;
}

function ThumbnailGrid({ items, totalCount }) {
  const visible = items.slice(0, 6);
  const overflow = totalCount - visible.length;
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
      {visible.map((it, idx) => (
        <button
          key={`${it.release_id}-${idx}`}
          type="button"
          onClick={() => it.release_id && navigate(`/catalog/${it.release_id}`)}
          className="aspect-square bg-zinc-950 border border-zinc-800/70 rounded overflow-hidden hover:border-rose-500/40 transition-colors relative group"
          title={it.release_name}
        >
          {it.model_image ? (
            <img src={resolveImageUrl(it.model_image)} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[8px] text-zinc-600 font-mono p-1 text-center break-all">
              {it.khmg_number || it.release_name?.slice(0, 14)}
            </div>
          )}
          {it.variant_label && it.variant_label !== "Regular" && (
            <span className={`absolute bottom-0.5 left-0.5 text-[8px] font-mono uppercase px-1 rounded ${
              it.variant_label === "Chase" ? "bg-amber-500/80 text-black" : "bg-purple-500/80 text-white"
            }`}>{it.variant_label}</span>
          )}
        </button>
      ))}
      {overflow > 0 && (
        <div className="aspect-square rounded border border-dashed border-zinc-800 flex items-center justify-center text-xs text-zinc-500 font-mono">+{overflow}</div>
      )}
    </div>
  );
}

function CommentsSection({ haul, viewerUserId, onCountChange }) {
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hauls/${haul.post_id}/comments`);
      setComments(data.comments || []);
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haul.post_id]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const body = input.trim();
    if (!body || submitting) return;
    if (body.length > 500) {
      toast.error("Comment too long (max 500)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/hauls/${haul.post_id}/comments`, { body });
      setComments((prev) => [...(prev || []), data]);
      setInput("");
      onCountChange?.(+1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await api.delete(`/hauls/${haul.post_id}/comments/${commentId}`);
      setComments((prev) => (prev || []).filter((c) => c.comment_id !== commentId));
      onCountChange?.(-1);
    } catch {
      toast.error("Couldn't delete");
    }
  };

  return (
    <div data-testid={`haul-comments-${haul.post_id}`} className="mt-3 pt-3 border-t border-zinc-800/60 space-y-2">
      {loading && (
        <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading comments...
        </div>
      )}
      {!loading && comments?.length === 0 && (
        <p className="text-[11px] text-zinc-600">Be the first to say something.</p>
      )}
      {comments?.map((c) => (
        <div key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="flex items-start gap-2 group">
          <div className="w-6 h-6 rounded-full bg-zinc-800 border border-zinc-700/70 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {c.user_photo ? (
              <img src={resolveImageUrl(c.user_photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="w-3 h-3 text-zinc-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1.5">
              <Link to={`/collector/${c.user_slug}`} className="text-xs font-semibold text-zinc-200 hover:text-rose-400 truncate">
                {c.user_name}
              </Link>
              <span className="text-[10px] text-zinc-600 font-mono">{relativeTime(c.created_at)}</span>
            </div>
            <p className="text-xs text-zinc-300 whitespace-pre-wrap break-words">{c.body}</p>
          </div>
          {viewerUserId === c.user_id && (
            <button
              type="button"
              onClick={() => handleDelete(c.comment_id)}
              data-testid={`delete-comment-${c.comment_id}`}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-600 hover:text-rose-400 flex-shrink-0"
              title="Delete comment"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
      {viewerUserId ? (
        <form onSubmit={handleSubmit} className="flex items-center gap-2 pt-1">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={500}
            placeholder="Add a comment..."
            disabled={submitting}
            data-testid={`comment-input-${haul.post_id}`}
            className="flex-1 text-xs bg-zinc-950 border border-zinc-800 focus:border-rose-500/40 rounded-lg px-3 py-1.5 text-zinc-200 placeholder-zinc-600 outline-none disabled:opacity-50"
          />
          <Button
            type="submit"
            size="sm"
            disabled={!input.trim() || submitting}
            data-testid={`comment-submit-${haul.post_id}`}
            className="bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-40"
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          </Button>
        </form>
      ) : (
        <p className="text-[11px] text-zinc-600 pt-1">
          <Link to="/login" className="text-rose-400 hover:underline">Sign in</Link> to comment.
        </p>
      )}
    </div>
  );
}

export default function HaulCard({ haul, showAuthor = true }) {
  const { user } = useAuth();
  const viewerUserId = user && user.user_id ? user.user_id : null;
  const [liked, setLiked] = useState(!!haul.liked_by_me);
  const [likeCount, setLikeCount] = useState(haul.like_count || 0);
  const [commentCount, setCommentCount] = useState(haul.comment_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [liking, setLiking] = useState(false);

  const handleLike = async () => {
    if (!viewerUserId) {
      toast.info("Sign in to like");
      return;
    }
    if (liking) return;
    setLiking(true);
    // optimistic
    const prevLiked = liked;
    const prevCount = likeCount;
    setLiked(!prevLiked);
    setLikeCount(prevCount + (prevLiked ? -1 : 1));
    try {
      const { data } = await api.post(`/hauls/${haul.post_id}/like`);
      setLiked(data.liked);
      setLikeCount(data.like_count);
    } catch {
      setLiked(prevLiked);
      setLikeCount(prevCount);
      toast.error("Couldn't update like");
    } finally {
      setLiking(false);
    }
  };

  return (
    <div data-testid={`haul-${haul.post_id}`} className="rounded-xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-rose-950/20 border border-rose-500/15 p-4">
      {showAuthor && (
        <div className="flex items-center justify-between mb-3">
          <AuthorHeader author={haul.author} createdAt={haul.created_at} />
          <span className="inline-flex items-center gap-1 text-[10px] text-amber-400/80 font-mono uppercase tracking-wider bg-amber-500/10 border border-amber-500/20 rounded-full px-2 py-0.5 flex-shrink-0">
            <Trophy className="w-3 h-3" /> Haul
          </span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        {!showAuthor && <Trophy className="w-4 h-4 text-amber-400 flex-shrink-0" />}
        <p className="text-sm text-zinc-100">
          <span className="font-semibold">{haul.total_count}</span> model{haul.total_count === 1 ? "" : "s"} added
          {haul.total_value > 0 && (
            <span className="text-zinc-500 ml-1.5">· <span className="font-mono text-emerald-400">${Number(haul.total_value).toFixed(haul.total_value >= 100 ? 0 : 2)}</span></span>
          )}
        </p>
        {!showAuthor && <span className="ml-auto text-[10px] text-zinc-600 font-mono flex-shrink-0">{new Date(haul.created_at).toLocaleDateString()}</span>}
      </div>
      {haul.caption && <p className="text-sm text-zinc-300 mb-3 whitespace-pre-wrap">{haul.caption}</p>}
      <ThumbnailGrid items={haul.items || []} totalCount={haul.total_count} />
      <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center gap-4">
        <button
          type="button"
          onClick={handleLike}
          disabled={liking}
          data-testid={`haul-like-btn-${haul.post_id}`}
          className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${
            liked ? "text-rose-400" : "text-zinc-400 hover:text-rose-400"
          }`}
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-rose-500" : ""}`} />
          <span data-testid={`haul-like-count-${haul.post_id}`}>{likeCount}</span>
        </button>
        <button
          type="button"
          onClick={() => setShowComments((v) => !v)}
          data-testid={`haul-comments-btn-${haul.post_id}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-sky-400 transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          <span data-testid={`haul-comment-count-${haul.post_id}`}>{commentCount}</span>
          <span className="ml-0.5">{showComments ? "Hide" : "Comment"}</span>
        </button>
      </div>
      {showComments && (
        <CommentsSection
          haul={haul}
          viewerUserId={viewerUserId}
          onCountChange={(delta) => setCommentCount((n) => Math.max(0, n + delta))}
        />
      )}
    </div>
  );
}
